import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Select from '@radix-ui/react-select';
import { useMemberAccessStore } from '@/hooks/useStores';
import { getAssignableRoles, getRoleDisplayName, type UserRole } from '@/utils/rolePermissions';
import type { ContentType, Role } from '@/types/member-access';
import Spinner from '@/components/shared/Spinner';
import '@/styles/form-select.css';
import './manage-content-permissions-modal.css';

interface ManageContentPermissionsModalProps {
  familyId: string;
  contentType: ContentType | string;
  contentId: string;
  contentLabel?: string;
  /** Whether the viewer may manage permissions for this item (effective owner/admin on it). */
  canManage: boolean;
  /** The viewer's effective role on the linked member, which caps the roles they may assign. */
  actorEffectiveRole: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const ManageContentPermissionsModal: React.FC<ManageContentPermissionsModalProps> = observer(
  ({ familyId, contentType, contentId, contentLabel, canManage, actorEffectiveRole, isOpen, onClose }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const accessStore = useMemberAccessStore();
    const roleOptions = getAssignableRoles(actorEffectiveRole);

    const typeLabelKey =
      contentType === 'story'
        ? 'contentAccess.typeMemory'
        : contentType === 'media_asset'
          ? 'contentAccess.typeMedia'
          : contentType === 'citation'
            ? 'contentAccess.typeCitation'
            : 'contentAccess.typeItem';
    const typeLabel = t(typeLabelKey);
    const titleLabel = contentLabel || t('contentAccess.thisItem', { type: typeLabel });

    const [addUserId, setAddUserId] = useState('');
    const [addRole, setAddRole] = useState<Role>('viewer');
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
      if (!isOpen || !canManage) return;
      setLocalError(null);
      setAddUserId('');
      setAddRole((roleOptions[roleOptions.length - 1] as Role) || 'viewer');
      void accessStore.loadContentAccess(familyId, contentType, contentId);
      return () => {
        accessStore.resetContentTarget();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when modal opens for this content
    }, [isOpen, familyId, contentType, contentId, canManage]);

    if (!isOpen) return null;

    if (!canManage) {
      return (
        <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
          <div className="v01-modal manage-content-permissions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-content-permissions-modal__header">
              <h2 className="v01-modal__title">{t('contentAccess.title')}</h2>
              <button type="button" onClick={onClose} className="v01-modal__close" aria-label={tCommon('close')}>
                ×
              </button>
            </div>
            <p className="manage-content-permissions-modal__lede">
              {t('contentAccess.cannotManage', { label: titleLabel })}
            </p>
            <div className="v01-modal__footer">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {tCommon('close')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    const available = accessStore.availableUsersForContent();
    const displayError = localError || accessStore.error;

    const handleAdd = async () => {
      if (!addUserId) return;
      setLocalError(null);
      try {
        await accessStore.createContentRole({ userId: addUserId, role: addRole });
        setAddUserId('');
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('contentAccess.failedAdd'));
      }
    };

    const handleRoleChange = async (userId: string, role: string) => {
      setLocalError(null);
      try {
        await accessStore.updateContentRole(userId, { role: role as Role });
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('contentAccess.failedUpdate'));
      }
    };

    const handleRemove = async (userId: string) => {
      if (!window.confirm(t('confirm.removePermission'))) {
        return;
      }
      setLocalError(null);
      try {
        await accessStore.deleteContentRole(userId);
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('contentAccess.failedRemove'));
      }
    };

    return (
      <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
        <div className="v01-modal manage-content-permissions-modal" onClick={(e) => e.stopPropagation()}>
          <div className="manage-content-permissions-modal__header">
            <h2 className="v01-modal__title">{t('contentAccess.title')}</h2>
            <button type="button" onClick={onClose} className="v01-modal__close" aria-label={tCommon('close')}>
              ×
            </button>
          </div>

          <p className="manage-content-permissions-modal__lede">{t('contentAccess.lede', { label: titleLabel })}</p>

          <div className="manage-content-permissions-modal__body">
            {accessStore.isLoading ? (
              <Spinner message={t('contentAccess.loading')} />
            ) : (
              <>
                {displayError && (
                  <p className="manage-content-permissions-modal__error" role="alert">
                    {displayError}
                  </p>
                )}

                {accessStore.contentAssignments.length === 0 ? (
                  <p className="manage-content-permissions-modal__empty">{t('contentAccess.empty')}</p>
                ) : (
                  <ul className="manage-content-permissions-modal__list">
                    {accessStore.contentAssignments.map((assignment) => (
                      <li key={assignment.userId} className="manage-content-permissions-modal__item">
                        <span className="manage-content-permissions-modal__name">
                          {accessStore.userLabel(assignment.userId)}
                        </span>
                        <div className="manage-content-permissions-modal__controls">
                          <Select.Root
                            value={assignment.role}
                            onValueChange={(value) => {
                              void handleRoleChange(assignment.userId, value);
                            }}
                            disabled={accessStore.isSaving}
                          >
                            <Select.Trigger
                              className="form-select-trigger manage-content-permissions-modal__select"
                              aria-label={t('contentAccess.roleForAria', {
                                name: accessStore.userLabel(assignment.userId)
                              })}
                            >
                              <Select.Value />
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                                {roleOptions.map((role) => (
                                  <Select.Item key={role} value={role} className="form-select-item">
                                    <Select.ItemText>{getRoleDisplayName(role as UserRole)}</Select.ItemText>
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={accessStore.isSaving}
                            onClick={() => {
                              void handleRemove(assignment.userId);
                            }}
                          >
                            {tCommon('remove')}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {available.length > 0 && (
                  <div className="manage-content-permissions-modal__add">
                    <h3 className="manage-content-permissions-modal__add-title">{t('contentAccess.addTitle')}</h3>
                    <div className="manage-content-permissions-modal__add-row">
                      <Select.Root value={addUserId || undefined} onValueChange={setAddUserId}>
                        <Select.Trigger
                          className="form-select-trigger manage-content-permissions-modal__select"
                          aria-label={t('contentAccess.userAria')}
                        >
                          <Select.Value placeholder={t('contentAccess.selectUser')} />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                            {available.map((user) => (
                              <Select.Item key={user.userId} value={user.userId} className="form-select-item">
                                <Select.ItemText>
                                  {user.memberName || user.nickname || user.email || t('contentAccess.userFallback')}
                                </Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                      <Select.Root value={addRole} onValueChange={(v) => setAddRole(v as Role)}>
                        <Select.Trigger
                          className="form-select-trigger manage-content-permissions-modal__select"
                          aria-label={t('contentAccess.roleAria')}
                        >
                          <Select.Value />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                            {roleOptions.map((role) => (
                              <Select.Item key={role} value={role} className="form-select-item">
                                <Select.ItemText>{getRoleDisplayName(role as UserRole)}</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={!addUserId || accessStore.isSaving}
                      onClick={() => {
                        void handleAdd();
                      }}
                    >
                      {t('actions.addPermission')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="v01-modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              {tCommon('close')}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default ManageContentPermissionsModal;
